import React from 'react';
import { Card, CardActionArea, CardContent, CardActions } from '@mui/material';
import cardClasses from './ContentCard.module.css';
import globalClasses from 'containers/Layouts/Layout.module.css';
import { ContentData } from 'model/ContentData';
import ContentService from 'services/ContentService';
import { useNavigate } from 'react-router-dom';
import i18n from 'i18n/I18nInstance';
import { trackPromise } from 'react-promise-tracker';
import ActionButtons from 'components/ActionButtons';
import PatientArticleData from 'model/PatientArticleData';
import PatientArticleService from 'services/PatientArticleService';
import VideoDetails from 'model/VideoDetails';
import AnalyticsService from 'services/AnalyticsService';
import { ContentType, EventType } from 'model/enums';
import ConfigurationService from 'services/ConfigurationService';

interface IProps {
    contentData: ContentData;
    hideActions: boolean;
}

const onDownloadButtonClicked = async (event, contentData: ContentData, mediaKey: string) => {
    event.stopPropagation();
    await trackPromise(ContentService.downloadPatientArticlePdf(mediaKey!, contentData.title!));
    AnalyticsService.recordEventType(contentData, EventType.Download, contentData.type);
};

const onPrintButtonClicked = async (event, contentData: ContentData, mediaKey: string) => {
    const customContentTypeId = 205;
    event.stopPropagation();

    if (contentData.contentTypeId === customContentTypeId) {
        await printCustomContent(mediaKey);
    } else {
        var iframe = buildFrame();
        if (contentData.type === ContentType.Document) {
            await buildArticleContent(iframe, mediaKey);
        } else {
            await buildVideoContent(iframe, mediaKey);
        }

        iframe.focus();
        setTimeout(() => {
            iframe.contentWindow!.print();
            iframe.remove();
        }, 500);
    }

    AnalyticsService.recordEventType(contentData, EventType.Print, contentData.type);
};

const buildFrame = (): HTMLIFrameElement => {
    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);
    iframe.setAttribute('id', 'ifrm');
    iframe.style.display = 'none';
    iframe.src = 'about:blank';

    iframe.contentDocument!.write('<head>');
    iframe.contentDocument!.write(
        `<style>@import url(https://fonts.googleapis.com/css?family=Lato&display=swap);</style>`,
    );
    iframe.contentDocument!.write(
        `<style>@import url(https://fonts.googleapis.com/css?family=Roboto&display=swap);</style>`,
    );
    iframe.contentDocument!.write(
        `<link rel="stylesheet" type="text/css" href="${process.env.PUBLIC_URL}/styles.css" />`,
    );
    iframe.contentDocument!.write('</head>');

    return iframe;
};

const printCustomContent = async (mediaKey: string) => {
    const patientArticleData: PatientArticleData = await trackPromise(
        ContentService.getPatientArticle(mediaKey),
    );

    const iframe = document.createElement('iframe');
    document.body.appendChild(iframe);
    iframe.setAttribute('id', 'ifrm');

    let pdfUrl = '';
    if (patientArticleData.pdfUrl) {
        const jwt = await ConfigurationService.retrieveSessionJwt();
        pdfUrl = `${patientArticleData.pdfUrl}&token=${jwt}`;
        iframe.src = pdfUrl;
    }

    iframe.focus();
    setTimeout(() => {
        iframe.contentWindow.onbeforeunload = iframe.remove;
        iframe.contentWindow.onafterprint = iframe.remove;
        iframe.contentWindow.print();
    }, 1000);
};

const buildArticleContent = async (iframe: HTMLIFrameElement, mediaKey: string) => {
    const patientArticleData: PatientArticleData = await trackPromise(
        ContentService.getPatientArticle(mediaKey),
    );

    const htmlContent = PatientArticleService.addPatientArticleStyleToHtml(
        patientArticleData.document!,
    );
    iframe.contentDocument!.write(`<body><div class="educationText">${htmlContent}</div></body>`);
};

const buildVideoContent = async (iframe: HTMLIFrameElement, mediaKey: string) => {
    let videoInfo: VideoDetails = await trackPromise(
        ContentService.getPatientEducationVideo(mediaKey),
    );
    iframe.contentDocument!.write('<body>');
    iframe.contentDocument!.write(`<div class="educationTitle">${videoInfo.title}</div>`);
    iframe.contentDocument!.write(`<div class="educationText">${videoInfo.blurb}</div>`);
    iframe.contentDocument!.write(`<div class="educationText">${videoInfo.transcript}</div>`);
    iframe.contentDocument!.write(`<div class="endStatement">${videoInfo.endStatement}</div>`);
    iframe.contentDocument!.write('</body>');
};

export default function ContentCard(props: IProps) {
    const navigate = useNavigate();
    const mediaKey = encodeURIComponent(props.contentData.mediaKey);
    const i18n_title = i18n.translator.formatMessage({
        id: 'contentCard.ContentTitle',
        defaultMessage: 'Content Title',
    });
    const i18n_date = i18n.translator.formatMessage({
        id: 'contentCard.PublishedDate',
        defaultMessage: 'Published Date',
    });
    const i18n_thumb = i18n.translator.formatMessage({
        id: 'contentCard.ContentThumbnail',
        defaultMessage: 'Content Thumbnail',
    });

    const cardClick = () => {
        AnalyticsService.recordEventType(
            props.contentData,
            EventType.Click,
            props.contentData.type,
        );
        navigate(`/${props.contentData.type.toString()}/${mediaKey}`);
    };

    const cardTitle = () => {
        return props.contentData.recalled
            ? `[Unassigned] ${props.contentData.title}`
            : props.contentData.title;
    };

    return (
        <React.Fragment>
            <Card role="article">
                <CardActionArea
                    className={
                        !props.contentData.recalled
                            ? cardClasses.parentActionArea
                            : cardClasses.disabledParentActionArea
                    }
                    onClick={(e) =>
                        !props.contentData.recalled ? cardClick() : e.preventDefault()
                    }
                >
                    <CardContent
                        data-qaid="actionArea1"
                        className={
                            cardClasses.actionArea1 +
                            ' ' +
                            (props.hideActions || props.contentData.recalled
                                ? ''
                                : cardClasses.cardWithoutActions)
                        }
                    >
                        <div className={cardClasses.titleDiv}>
                            <h6
                                data-qaid="cardTitle"
                                className={globalClasses.typography_h6Headline}
                                aria-label={`${i18n_title} : ${cardTitle()}`}
                            >
                                {cardTitle()}
                            </h6>
                            <div
                                data-qaid="cardDate"
                                className={globalClasses.typography_body2}
                                aria-label={`${i18n_date} : ${props.contentData.date.toLocaleDateString()}`}
                            >
                                {props.contentData.date.toLocaleDateString()}
                            </div>
                        </div>
                        <div className={cardClasses.thumbnailDiv}>
                            <img
                                data-qaid="cardThumbnail"
                                alt={i18n_thumb}
                                aria-label={
                                    props.contentData.type === ContentType.Document
                                        ? 'Document'
                                        : 'Video'
                                }
                                src={
                                    props.contentData.type === ContentType.Document
                                        ? './img/thumb-article.svg'
                                        : './img/thumb-video.svg'
                                }
                            ></img>
                        </div>
                    </CardContent>
                </CardActionArea>
                {!props.hideActions && !props.contentData.recalled && (
                    <CardActions data-qaid="actionArea2" className={cardClasses.actionArea2}>
                        <ActionButtons
                            downloadHandler={(e) =>
                                onDownloadButtonClicked(e, props.contentData, mediaKey)
                            }
                            printHandler={(e) =>
                                onPrintButtonClicked(e, props.contentData, mediaKey)
                            }
                            title={cardTitle()}
                        ></ActionButtons>
                    </CardActions>
                )}
            </Card>
        </React.Fragment>
    );
}
